import React from 'react';
import { ChartRendererSupabase } from './ChartRendererSupabase';
import {
  ClimateEquityDataService,
  EquityDataPoint,
  EquityDataResolution
} from '../../services/climateEquityDataService';

interface EquityHeatmapChartProps {
  data: EquityDataPoint[];
  resolution: EquityDataResolution;
  loading?: boolean;
  error?: string | null;
}

const EquityHeatmapChart: React.FC<EquityHeatmapChartProps> = ({
  data,
  resolution,
  loading = false,
  error = null
}) => {
  if (loading) {
    return (
      <div className="chart-loading" style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-spinner"></div>
        <p>Loading climate equity analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error" style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#dc2626' }}>{error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="chart-no-data" style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>No equity data available for this location.</p>
      </div>
    );
  }

  // Transform data using service layer
  let chartData, chartOptions;
  try {
    chartData = ClimateEquityDataService.getHeatmapChartData(data);
    chartOptions = ClimateEquityDataService.getChartOptions('heatmap', data, resolution);
  } catch (transformError) {
    return (
      <div className="chart-error" style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#dc2626' }}>Error processing chart data: {transformError instanceof Error ? transformError.message : 'Unknown error'}</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Resolution info */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        alignItems: 'center',
        marginBottom: '1rem',
        padding: '0.5rem',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '0.5rem'
      }}>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          Heatmap: Education (x-axis) vs Solar (y-axis) with EV adoption intensity (color/opacity)
        </span>
        <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: 'auto' }}>
          ({data.length} {resolution === 'city' ? 'cities' : 'census tracts'} aggregated into grid)
        </span>
      </div>

      {/* Heatmap Legend */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1rem',
        gap: '1rem'
      }}>
        <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
          EV Adoption Intensity:
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '20px',
            height: '12px',
            backgroundColor: 'rgba(220, 38, 38, 0.2)',
            border: '1px solid #6b7280'
          }} />
          <span style={{ fontSize: '10px', color: '#6b7280' }}>Low</span>
          <div style={{
            width: '20px',
            height: '12px',
            backgroundColor: 'rgba(220, 38, 38, 1)',
            border: '1px solid #6b7280'
          }} />
          <span style={{ fontSize: '10px', color: '#6b7280' }}>High</span>
        </div>
      </div>

      <ChartRendererSupabase
        chartData={{
          ...chartData,
          type: 'scatter' // Heatmap uses scatter with sized points
        }}
        chartOptions={{
          ...chartOptions,
          type: 'scatter' // Heatmap uses scatter with sized points
        }}
        height="400px"
      />
    </div>
  );
};

export default EquityHeatmapChart;